import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Zap, Trash2, Copy, Printer, FileText } from "lucide-react";
import { getAuditType } from "@/lib/auditTypes";
import { toast } from "sonner";
import { useProtectedAssetUrl } from "@/hooks/useProtectedAssetUrl";
import { downloadProtectedFile } from "@/lib/assets";

function markdownToHtml(md) {
  if (!md) return "";
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  let inTable = false;
  let tableRows = [];

  const flushList = () => { if (inList) { html += "</ul>"; inList = false; } };
  const flushTable = () => {
    if (inTable && tableRows.length) {
      html += "<table>";
      tableRows.forEach((r, i) => {
        const cells = r.split("|").map((c) => c.trim()).filter((c) => c.length);
        if (cells.every((c) => /^-+$/.test(c))) return;
        const tag = i === 0 ? "th" : "td";
        html += "<tr>" + cells.map((c) => `<${tag}>${c}</${tag}>`).join("") + "</tr>";
      });
      html += "</table>";
      tableRows = []; inTable = false;
    }
  };

  const inlineFormat = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
     .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
     .replace(/`([^`]+)`/g, "<code>$1</code>");

  for (const raw of lines) {
    const l = raw.trimEnd();
    if (l.startsWith("|") && l.endsWith("|")) { inTable = true; tableRows.push(l); continue; }
    else { flushTable(); }
    if (l.startsWith("## ")) { flushList(); html += `<h2>${inlineFormat(l.slice(3))}</h2>`; }
    else if (l.startsWith("### ")) { flushList(); html += `<h3>${inlineFormat(l.slice(4))}</h3>`; }
    else if (l.startsWith("# ")) { flushList(); html += `<h2>${inlineFormat(l.slice(2))}</h2>`; }
    else if (l.startsWith("- ") || l.startsWith("* ")) {
      if (!inList) { html += "<ul>"; inList = true; }
      html += `<li>${inlineFormat(l.slice(2))}</li>`;
    } else if (!l.trim()) { flushList(); }
    else { flushList(); html += `<p>${inlineFormat(l)}</p>`; }
  }
  flushTable();
  flushList();
  return html;
}

export default function CustomReportPage() {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const { assetUrl: logoUrl } = useProtectedAssetUrl(
    audit?.branding?.has_logo ? `/branding/${audit.branding.tenant_id}/logo` : null,
    !!audit?.branding?.has_logo
  );

  const load = useCallback(() => api.get(`/audits/${id}`).then((r) => setAudit(r.data)).catch(() => toast.error("Errore caricamento")), [id]);
  useEffect(() => { load(); }, [load]);

  const clearReport = async () => {
    try {
      await api.delete(`/audits/${id}/custom-report`);
      toast.success("Verbale custom eliminato");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(audit?.custom_report || "");
      toast.success("Markdown copiato negli appunti");
    } catch { toast.error("Copia non riuscita"); }
  };

  const downloadDocx = async () => {
    try {
      await downloadProtectedFile(
        `/audits/${id}/docx?source=custom_report`,
        `verbale-custom-${(audit?.company_name || "audit").replace(/[^\w\-]+/g, "_").slice(0, 40)}-v${audit?.version || 1}.docx`
      );
    } catch (err) { toast.error(getApiErrorMessage(err, "Errore download Word")); }
  };

  if (!audit) return <div className="p-10 text-slate-500">Caricamento…</div>;
  if (!audit.custom_report) {
    return (
      <div className="p-10 max-w-3xl">
        <Link to={`/audit/${id}`} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6"><ArrowLeft className="h-4 w-4" /> Torna all'audit</Link>
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-16 text-center">
          <Zap className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display font-bold text-lg mb-2">Nessun verbale generato con il tuo OpenAI</div>
          <p className="text-slate-500 text-sm">Torna all'audit e clicca "Il mio OpenAI" per generarlo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-16">
      <div className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="p-5 md:px-10 max-w-5xl flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={`/audit/${id}`} className="text-slate-500 hover:text-slate-900 transition-colors duration-200"><ArrowLeft className="h-5 w-5" /></Link>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-600 inline-flex items-center gap-1"><Zap className="h-3 w-3" /> Verbale custom (OpenAI)</div>
              <h1 className="font-display font-black text-xl md:text-2xl truncate">{audit.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadDocx} data-testid="download-docx-btn" className="h-10 bg-slate-900 text-white hover:bg-slate-800 border-slate-900"><FileText className="h-4 w-4" /> Word</Button>
            <Button variant="outline" onClick={() => window.print()} data-testid="print-btn" className="h-10"><Printer className="h-4 w-4" /> Stampa</Button>
            <Button variant="outline" onClick={copyMarkdown} data-testid="copy-md-btn" className="h-10"><Copy className="h-4 w-4" /> Copia MD</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="delete-custom-btn" className="h-10 text-red-700 border-red-300 hover:bg-red-50"><Trash2 className="h-4 w-4" /> Elimina</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Eliminare il verbale custom?</AlertDialogTitle></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={clearReport} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div className="p-5 md:p-10 max-w-5xl">
        {/* Branded header */}
        {(audit.branding?.has_logo || audit.branding?.header_text) && (
          <div data-testid="branded-header" className="bg-white border border-slate-200 rounded-t-lg p-6 border-b-4" style={{ borderBottomColor: audit.branding?.accent_color || "#4F46E5" }}>
            <div className="flex items-start gap-6 flex-wrap">
              {audit.branding?.has_logo && (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="max-h-20 max-w-[240px] object-contain"
                />
              )}
              {audit.branding?.header_text && (
                <div className="flex-1 min-w-0 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {audit.branding.header_text}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={(audit.branding?.has_logo || audit.branding?.header_text) ? "" : "mb-6"}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 mt-6">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Azienda</div>
            <div className="font-semibold text-sm mt-1 truncate">{audit.company_name}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Modello OpenAI</div>
            <div className="font-mono-plex text-xs mt-1 truncate">{audit.custom_report_model || "gpt-4o"}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Framework</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {(audit.types || [audit.type]).filter(Boolean).map((t) => {
                const meta = getAuditType(t);
                return <span key={t} className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${meta.badge_bg} ${meta.badge_text}`}>{meta.label}</span>;
              })}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Generato</div>
            <div className="font-mono-plex text-xs mt-1">{audit.custom_report_at?.slice(0, 16).replace("T", " ")}</div>
          </div>
        </div>
        </div>

        <article
          data-testid="custom-report-content"
          className="report-content bg-white border border-indigo-200 rounded-lg p-8 md:p-12 leading-relaxed"
          style={audit.branding?.accent_color ? { "--accent": audit.branding.accent_color } : undefined}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(markdownToHtml(audit.custom_report), { USE_PROFILES: { html: true } }) }}
        />

        {audit.branding?.footer_text && (
          <div data-testid="branded-footer" className="mt-6 bg-white border border-slate-200 rounded-b-lg p-5 text-xs text-slate-500 whitespace-pre-wrap leading-relaxed">
            {audit.branding.footer_text}
            {audit.branding?.website && (
              <div className="mt-2">
                <a href={audit.branding.website} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">{audit.branding.website}</a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
